import React, { memo, useCallback, useMemo } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useSelector } from 'react-redux'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { Button, Col, Row } from 'antd'
import {
  find,
  findIndex,
  includes,
  isEmpty,
  isNil,
  map,
  reduce,
  size as _size,
  toString,
} from 'lodash'
import PropTypes from 'prop-types'

import { useQueryParams } from '../../../core/hooks'
import { areSameValue } from '../../../core/utils'

import { PAGINATION } from '../../../common/constants'
import { getTreeIdsFromLevelToLevel } from '../../../common/helpers'
import { flattenTreeStructure } from '../../../common/utils'

import { MENU_CURATION_TASK_STATUS } from '../../../menu-curation-tasks/constants'
import {
  DEFAULT_DISH_LIST_FILTERS,
  DISHES_AVAILABLE_HOTKEYS,
} from '../../constants'
import { StyledMenuTitleFilterTreeSelect } from '../styled'

const DishMenuTitleFilter = ({ isDisabled, size }) => {
  const { menuTitleId = DEFAULT_DISH_LIST_FILTERS.MENU_TITLE, setQueryParams } =
    useQueryParams()

  const { data: menuCurationTask } = useSelector(
    store => store.menuCurationTasks.menuCurationTask,
  )

  const { menuTitleDishCounts } = useSelector(store => store.menuTitles)

  const { menuTitles } = menuCurationTask?.menu || {}

  const isTaskInProgress = useMemo(
    () =>
      includes(
        [
          MENU_CURATION_TASK_STATUS.TIER_ONE_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.CURATION_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.QA_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.QC_IN_PROGRESS.value,
        ],
        menuCurationTask?.taskStatus,
      ),
    [menuCurationTask],
  )

  const menuTitleOptions = useMemo(() => {
    const buildTree = cMenuTitle => {
      if (isEmpty(cMenuTitle)) {
        return {}
      }

      const children = map(cMenuTitle.children, child => buildTree(child))
      const count =
        find(menuTitleDishCounts, ({ menuTitleId }) =>
          areSameValue(menuTitleId, cMenuTitle.id),
        )?.count || 0
      const dishCount =
        count + reduce(children, (acc, child) => acc + child.dishCount, 0)

      return {
        ...cMenuTitle,
        title: `${cMenuTitle.title}${
          isTaskInProgress ? ` (${dishCount})` : ''
        }`,
        value: cMenuTitle.id,
        dishCount,
        children,
      }
    }
    return map(menuTitles, rootMenu => buildTree(rootMenu))
  }, [isTaskInProgress, menuTitleDishCounts, menuTitles])

  const treeDefaultExpandedKeys = useMemo(
    () => getTreeIdsFromLevelToLevel(menuTitleOptions, 1, 1),
    [menuTitleOptions],
  )

  const menuTitleIdsForNavigation = useMemo(
    () => map(flattenTreeStructure(menuTitleOptions), 'id'),
    [menuTitleOptions],
  )

  const { nextMenuTitleId, prevMenuTitleId } = useMemo(() => {
    const currentIndex = findIndex(menuTitleIdsForNavigation, id =>
      areSameValue(id, menuTitleId),
    )

    if (
      currentIndex === -1 ||
      isNil(menuTitleId) ||
      isEmpty(menuTitleIdsForNavigation)
    ) {
      return {
        nextMenuTitleId: null,
        prevMenuTitleId: null,
      }
    }

    return {
      nextMenuTitleId:
        menuTitleIdsForNavigation[
          currentIndex === _size(menuTitleIdsForNavigation) - 1
            ? null
            : currentIndex + 1
        ],
      prevMenuTitleId:
        menuTitleIdsForNavigation[currentIndex === 0 ? null : currentIndex - 1],
    }
  }, [menuTitleIdsForNavigation, menuTitleId])

  const isBackNavigationDisabled = useMemo(
    () => isNil(prevMenuTitleId),
    [prevMenuTitleId],
  )
  const isForwardNavigationDisabled = useMemo(
    () => isNil(nextMenuTitleId),
    [nextMenuTitleId],
  )

  const setDishMenuTitleFilter = useCallback(
    value =>
      setQueryParams({
        currentPage: PAGINATION.DEFAULT_PAGE,
        menuTitleId: value,
      }),
    [setQueryParams],
  )

  const navigateMenuTitles = useCallback(
    isNext => {
      if (isNext && !isForwardNavigationDisabled) {
        return setDishMenuTitleFilter(nextMenuTitleId)
      }

      if (!isNext && !isBackNavigationDisabled) {
        return setDishMenuTitleFilter(prevMenuTitleId)
      }

      if (!isNil(menuTitleIdsForNavigation[0])) {
        return setDishMenuTitleFilter(menuTitleIdsForNavigation[0])
      }

      return null
    },
    [
      menuTitleIdsForNavigation,
      nextMenuTitleId,
      prevMenuTitleId,
      setDishMenuTitleFilter,
      isBackNavigationDisabled,
      isForwardNavigationDisabled,
    ],
  )

  const navigateForward = useCallback(
    () => navigateMenuTitles(true),
    [navigateMenuTitles],
  )

  const navigateBackward = useCallback(
    () => navigateMenuTitles(false),
    [navigateMenuTitles],
  )

  useHotkeys(
    DISHES_AVAILABLE_HOTKEYS.ALT_RIGHT.hotkey,
    e => {
      e.preventDefault()
      navigateMenuTitles(true)
    },
    [navigateMenuTitles],
  )

  useHotkeys(
    DISHES_AVAILABLE_HOTKEYS.ALT_LEFT.hotkey,
    e => {
      e.preventDefault()
      navigateMenuTitles(false)
    },
    [navigateMenuTitles],
  )

  return (
    <Row justify="space-between">
      <Col flex="0">
        <Button
          icon={<LeftOutlined />}
          disabled={isBackNavigationDisabled}
          onClick={navigateBackward}
          size={size}
        />
      </Col>
      <Col flex="1">
        <StyledMenuTitleFilterTreeSelect
          dropdownMatchSelectWidth={false}
          treeNodeLabelProp="title"
          disabled={isDisabled}
          treeDefaultExpandedKeys={treeDefaultExpandedKeys}
          treeData={menuTitleOptions}
          value={menuTitleId && toString(menuTitleId)}
          onChange={setDishMenuTitleFilter}
          showSearch
          allowClear
          placeholder="Filter by Menu Titles..."
          size={size}
        />
      </Col>
      <Col flex="0">
        <Button
          icon={<RightOutlined />}
          disabled={isForwardNavigationDisabled}
          onClick={navigateForward}
          size={size}
        />
      </Col>
    </Row>
  )
}

DishMenuTitleFilter.propTypes = {
  isDisabled: PropTypes.bool,
  size: PropTypes.string,
}

DishMenuTitleFilter.defaultProps = {
  isDisabled: false,
  size: undefined,
}

export default memo(DishMenuTitleFilter)
